<template>
  <span
    v-if="variant === 'short'"
    class="pr-penetration pr-penetration--short"
    :title="rangeSummary"
  >
    <span class="pr-penetration__mini-track" aria-hidden="true">
      <span
        v-if="positionPct != null"
        class="pr-penetration__marker pr-penetration__marker--mini"
        :style="markerStyle"
      />
    </span>
    <span class="pr-penetration__label">{{ label }}</span>
  </span>

  <div v-else class="pr-penetration pr-penetration--full" role="group" :aria-label="ariaLabel">
    <div class="pr-penetration__track" aria-hidden="true">
      <span
        v-if="positionPct != null"
        class="pr-penetration__marker"
        data-test="penetration-marker"
        :style="markerStyle"
      />
    </div>
    <div class="pr-penetration__ticks">
      <span class="pr-penetration__tick pr-penetration__tick--min">
        <span class="pr-penetration__tick-name">Min</span>
        <span class="pr-penetration__tick-amount">{{ minText }}</span>
      </span>
      <span class="pr-penetration__tick pr-penetration__tick--mid">
        <span class="pr-penetration__tick-name">Mid</span>
        <span class="pr-penetration__tick-amount">{{ midText }}</span>
      </span>
      <span class="pr-penetration__tick pr-penetration__tick--max">
        <span class="pr-penetration__tick-name">Max</span>
        <span class="pr-penetration__tick-amount">{{ maxText }}</span>
      </span>
    </div>
    <p v-if="showLabel" class="pr-penetration__label" data-test="penetration-label">{{ label }}</p>
  </div>
</template>

<script setup>
import { computed } from "vue";

import {
  formatPenetrationAmount,
  formatPenetrationLabel,
  normalizePositionPct,
} from "./penetrationFormat";

const props = defineProps({
  rangePenetration: {
    type: Object,
    default: null,
  },
  currencySymbol: {
    type: String,
    default: "$",
  },
  /** Hide the "N% of range" line when there is no amount to place (e.g. a range-only view). */
  showLabel: {
    type: Boolean,
    default: true,
  },
  variant: {
    type: String,
    default: "full",
    validator: (value) => ["full", "short"].includes(value),
  },
});

const positionPct = computed(() => normalizePositionPct(props.rangePenetration));
const label = computed(() => formatPenetrationLabel(props.rangePenetration));
const markerStyle = computed(() => ({ "--pr-penetration-position": `${positionPct.value}%` }));

const minText = computed(() => formatPenetrationAmount(props.rangePenetration?.min, props.currencySymbol));
const midText = computed(() => formatPenetrationAmount(props.rangePenetration?.mid, props.currencySymbol));
const maxText = computed(() => formatPenetrationAmount(props.rangePenetration?.max, props.currencySymbol));

const rangeSummary = computed(
  () => `Min ${minText.value} · Mid ${midText.value} · Max ${maxText.value}`,
);
const ariaLabel = computed(() => `Position in range: ${label.value}. ${rangeSummary.value}`);
</script>

<style scoped>
.pr-penetration {
  font-family: Inter, sans-serif;
  color: #344054;
  box-sizing: border-box;
}

.pr-penetration--full {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 160px;
  align-self: stretch;
}

.pr-penetration--short {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  white-space: nowrap;
}

.pr-penetration__track,
.pr-penetration__mini-track {
  position: relative;
  border-radius: 8px;
  background: linear-gradient(to right, #d9d9d9 33.33%, #f4f4f4 33.33% 66.66%, #fbfbfb 66.66%);
  box-shadow: inset 0 0 0 1px #d0d5dd;
}

.pr-penetration__track {
  height: 8px;
}

.pr-penetration__mini-track {
  display: inline-block;
  width: 40px;
  height: 6px;
  flex-shrink: 0;
}

.pr-penetration__marker {
  position: absolute;
  top: 50%;
  left: var(--pr-penetration-position);
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #175cd3;
  border: 2px solid #fff;
  box-sizing: border-box;
  transform: translate(-50%, -50%);
}

.pr-penetration__marker--mini {
  width: 10px;
  height: 10px;
}

.pr-penetration__ticks {
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  line-height: 16px;
}

.pr-penetration__tick {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.pr-penetration__tick--mid {
  align-items: center;
}

.pr-penetration__tick--max {
  align-items: flex-end;
}

.pr-penetration__tick-name {
  font-weight: 500;
}

.pr-penetration__label {
  margin: 0;
  font-size: 12px;
  font-weight: 500;
  line-height: 16px;
}
</style>
